import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  color: #666;
  line-height: 1.6;
`;

const ProcessSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 3rem 0;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepCard = styled.div`
  background-color: #fff;
  border-radius: 12px;
  padding: 2rem;
  flex: 1;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
`;

const StepTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #333;
`;

const StepText = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
`;

const WhyJoinSection = styled.section`
  margin: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #333;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
`;

const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const BenefitText = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
`;

const CTASection = styled.section`
  text-align: center;
  margin: 4rem 0 2rem;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: #291759;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color:white;
    background-color: #291759;
    transform: translateY(-2px);
  }
`;

const BecomeNutritionist = () => {
  return (
    <PageContainer>
      <HeroSection>
        <Title>Bli en Wonder Klinik</Title>
        <Subtitle>
          Join our network of nutrition experts and help clients achieve their health goals
          using our innovative tools and platform
        </Subtitle>
      </HeroSection>

      <ProcessSection>
        <StepCard>
          <StepNumber>1</StepNumber>
          <StepTitle>Apply Online</StepTitle>
          <StepText>
            Complete our application form with your credentials, experience, and specialties.
          </StepText>
        </StepCard>
        
        <StepCard>
          <StepNumber>2</StepNumber>
          <StepTitle>Verification</StepTitle>
          <StepText>
            Our team will review your credentials and verify your professional experience.
          </StepText>
        </StepCard>
        
        <StepCard>
          <StepNumber>3</StepNumber>
          <StepTitle>Start Practicing</StepTitle>
          <StepText>
            Once approved, set up your profile and begin accepting client consultations.
          </StepText>
        </StepCard>
      </ProcessSection>

      <WhyJoinSection>
        <SectionTitle>Why Join GeoDiet?</SectionTitle>
        <BenefitsGrid>
          <BenefitCard>
            <IconContainer>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 6V12L16 14" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconContainer>
            <BenefitContent>
              <BenefitTitle>Flexibility</BenefitTitle>
              <BenefitText>
                Set your own schedule and work remotely from anywhere with our user-friendly platform.
              </BenefitText>
            </BenefitContent>
          </BenefitCard>
          
          <BenefitCard>
            <IconContainer>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconContainer>
            <BenefitContent>
              <BenefitTitle>Growing Client Base</BenefitTitle>
              <BenefitText>
                Access a growing community of clients seeking personalized nutrition guidance.
              </BenefitText>
            </BenefitContent>
          </BenefitCard>
          
          <BenefitCard>
            <IconContainer>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H9H8" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconContainer>
            <BenefitContent>
              <BenefitTitle>Advanced Tools</BenefitTitle>
              <BenefitText>
                Utilize our fitness data integration and analytics to provide data-driven nutrition advice.
              </BenefitText>
            </BenefitContent>
          </BenefitCard>
          
          <BenefitCard>
            <IconContainer>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1V23" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconContainer>
            <BenefitContent>
              <BenefitTitle>Competitive Compensation</BenefitTitle>
              <BenefitText>
                Earn competitive rates with our transparent fee structure and regular payment schedule.
              </BenefitText>
            </BenefitContent>
          </BenefitCard>
        </BenefitsGrid>
      </WhyJoinSection>

      <CTASection>
        <CTAButton to="/partner-signup">Apply Now</CTAButton>
      </CTASection>
    </PageContainer>
  );
};

export default BecomeNutritionist; 